import { Box, Grid, makeStyles, Table, TableBody, TableCell, TableRow, Typography } from "@material-ui/core";
import Highcharts from "highcharts";
import React, { ReactNode, useEffect, useState } from "react";
import NumberFormat from "react-number-format";
import * as yup from "yup";
import { CalculateBalanceQuery, InvestmentOptions, Product, SchemeType } from "../../api";
import { useProductStyles } from "../../common/sharedStyles";
import { useAppDispatch, useAppSelector } from "../../store";
import { calculateBalanceAsync } from "../../store/calculate";
import FormikWrapper from "../containers/FormikWrapper";
import { Colors, DialogBox, SectionTitleText, SubtitleText, TitleText } from "../controls/helpers";
import Chart, { defaultMarker, Series } from "../controls/helpers/Chart";
import { CurrencyTextInput, NumberTextInput, Select } from "../controls/inputs";

export enum CompareType {
  Strategies = "Strategies",
  ContributionPercent = "ContributionPercent",
  ContributionDollars = "ContributionDollars",
  RetirementAge = "RetirementAge",
  Drawdown = "Drawdown",
}

export enum EmploymentStatus {
  Employed = "Employed",
  SelfEmployed = "Self-employed",
}

interface DrawdownValues {
  weeklyIncome?: number;
  remainingBalance: number;
}

const INFLATION_RATE = 0.02;
const INTEREST_RATE = 0.025;
const RETIREMENT_YEARS = 25;
const AGES_DRAWDOWN = [65];
const AGES_RETIREMENT = [65, 70, 75];

const STRATEGIES = [
  { color: Colors.Green, name: SchemeType.Growth },
  { color: "#61CE85", name: SchemeType.Inflation },
  { color: "#199141", name: SchemeType.Income },
];

const CONTRIBUTIONS = [
  { color: Colors.Green, name: "10%" },
  { color: "#61CE85", name: "8%" },
  { color: "#199141", name: "6%" },
  { color: "#0C451F", name: "4%" },
  { color: "#20452D", name: "3%" },
];

const CONTRIBUTION_RATES = [
  { label: "3%", value: 0.03 },
  { label: "4%", value: 0.04 },
  { label: "6%", value: 0.06 },
  { label: "8%", value: 0.08 },
  { label: "10%", value: 0.1 },
];

const getComparisonOptions = (ageInScenario: number, retirementAge: number, employmentStatus?: EmploymentStatus) => {
  const options: Array<{ label: string; value: CompareType }> = [{ label: CompareType.Strategies, value: CompareType.Strategies }];

  if (employmentStatus === EmploymentStatus.Employed)
    options.push({
      label: "Contribution %",
      value: CompareType.ContributionPercent,
    });
  else
    options.push({
      label: "Contribution $",
      value: CompareType.ContributionDollars,
    });

  if (ageInScenario <= retirementAge) options.push({ label: "Retirement age", value: CompareType.RetirementAge });
  if (ageInScenario <= retirementAge) options.push({ label: CompareType.Drawdown, value: CompareType.Drawdown });
  return options;
};

const getDefaultSettings = (employed?: EmploymentStatus, compareType?: CompareType, annualIncome?: number) => ({
  ageInScenario: 25,
  employmentStatus: employed || EmploymentStatus.Employed,
  kiwiSaverBalance: 20000,
  grossAnnualIncome: annualIncome || (employed === EmploymentStatus.SelfEmployed ? 100000 : 75000),
  contributionRate: 0.04,
  compareType: compareType || CompareType.Strategies,
  employerContribution: 0.03,
  kiwiSaverVoluntaryAnnualContribution: employed === EmploymentStatus.SelfEmployed ? 3000 : 0,
  retirementAge: 65,
  schemeType: SchemeType.Growth,
});

const sortNumbersDescending = (array: number[]) => {
  array.sort((a, b) => (a > b ? -1 : 1));
  return array;
};

const TableText = ({ children }: { children: ReactNode }) => <Typography style={{ color: Colors.Black, fontSize: "1rem", fontWeight: 500 }}>{children}</Typography>;

export default function KiwiSaverCalculator({
  annualIncome,
  backgroundColor,
  employmentStatus,
  compareType,
  investmentOption,
  updateAnnualIncome,
  updateAge,
  updateKiwiSaverBalance,
}: {
  annualIncome?: number;
  backgroundColor?: string;
  employmentStatus?: EmploymentStatus;
  compareType?: CompareType;
  investmentOption: InvestmentOptions;
  updateAnnualIncome?: (annualIncome?: number) => void;
  updateAge?: (age?: number) => void;
  updateKiwiSaverBalance?: (kiwiSaverBalance?: number) => void;
}) {
  const { calculateBalances: kiwiSaverBalances } = useAppSelector((state) => state.calculate);
  const dispatch = useAppDispatch();
  const productClasses = useProductStyles({ product: Product.KiwiSaver });
  const [series, setSeries] = useState<Series[]>([]);
  const [prevValues, setPrevValues] = useState<any>();
  const [showAssumptions, setShowAssumptions] = useState<boolean>(false);
  const [showDrawdownAssumptions, setShowDrawdownAssumptions] = useState<boolean>(false);
  const [showDrawdownTable, setShowDrawdownTable] = useState<boolean>(compareType === CompareType.Drawdown);
  const [drawdownValues, setDrawdownValues] = useState<{
    sixPercent: DrawdownValues[];
    fixedDate: DrawdownValues[];
  }>({ sixPercent: [], fixedDate: [] });
  const [contributionDollarsAllocation, setContributionDollarsAllocation] = useState<number[]>([]);
  const defaultSettings = getDefaultSettings(employmentStatus, compareType, annualIncome);
  const classes = useStyles();

  // Runs on page load
  useEffect(() => {
    updateAnnualIncome?.(defaultSettings.grossAnnualIncome);
    updateAge?.(defaultSettings.ageInScenario);
    updateKiwiSaverBalance?.(defaultSettings.kiwiSaverBalance);
    calculate(defaultSettings);
    let timeout: number | undefined;

    const handleResize = () => {
      window.clearTimeout(timeout);
      timeout = window.setTimeout(() => {
        Highcharts.charts.forEach((chart) => !!chart && chart.reflow());
      }, 333);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  function calculateDrawdown(balance: number) {
    setDrawdownValues({
      sixPercent: calculateSixPercentDrawdown(balance),
      fixedDate: calculateAnnualWithdrawalDrawdown(balance),
    });
  }

  function calculateSixPercentDrawdown(balance: number) {
    const values: DrawdownValues[] = [];
    const withdrawalRate = 0.06;
    let remainingBalance = balance;
    values.push({ remainingBalance: Math.round(balance! / 1000) * 1000 });

    // Up to 20 years or remaining balance is gets to zero
    for (let i = 1; i <= RETIREMENT_YEARS && remainingBalance > 0; i++) {
      const yearlyIncome = remainingBalance * withdrawalRate;
      remainingBalance = ((remainingBalance - yearlyIncome) * (1 + INTEREST_RATE)) / (1 + INFLATION_RATE);
      values.push({ weeklyIncome: yearlyIncome / 52.1429, remainingBalance });
    }

    return values;
  }

  function calculateAnnualWithdrawalDrawdown(balance: number) {
    const values: DrawdownValues[] = [];
    let remainingBalance = balance;
    values.push({ remainingBalance: Math.round(balance! / 1000) * 1000 });

    for (let i = 1; i <= RETIREMENT_YEARS; i++) {
      let yearsLeft = RETIREMENT_YEARS - i + 1;
      let yearlyIncome = remainingBalance / yearsLeft;
      remainingBalance -= yearlyIncome;
      remainingBalance *= (1 + INTEREST_RATE) / (1 + INFLATION_RATE);
      values.push({ weeklyIncome: yearlyIncome / 52.1429, remainingBalance });
    }

    return values;
  }

  const calculate = (values: any) => {
    setPrevValues(values);
    setSeries([]);

    const request: CalculateBalanceQuery = {
      ageInScenario: values.ageInScenario,
      retirementAge: defaultSettings.retirementAge,
      grossAnnualIncome: values.employmentStatus === EmploymentStatus.Employed ? (values.grossAnnualIncome ? parseFloat(values.grossAnnualIncome.toString().replace(/,/g, "")) : 0) : 100000,
      kiwiSaverBalance: values.kiwiSaverBalance ? parseFloat(values.kiwiSaverBalance.toString().replace(/,/g, "")) : 0,
      kiwiSaverEmployeeContribution: values.employmentStatus === EmploymentStatus.Employed ? values.contributionRate : 0,
      kiwiSaverEmployerContribution: values.employmentStatus === EmploymentStatus.Employed ? defaultSettings.employerContribution : 0,
      kiwiSaverVoluntaryAnnualContribution:
        values.employmentStatus === EmploymentStatus.Employed
          ? 0
          : values.kiwiSaverVoluntaryAnnualContribution
          ? parseFloat(values.kiwiSaverVoluntaryAnnualContribution.toString().replace(/,/g, ""))
          : 0,
      schemeType: investmentOption === InvestmentOptions.SelfSelect ? SchemeType.Growth : SchemeType.LifeCycle,
    };

    switch (values.compareType) {
      case CompareType.Strategies:
        dispatch(
          calculateBalanceAsync(
            STRATEGIES.map((strategy) => ({
              ...request,
              schemeType: strategy.name,
            }))
          )
        );
        break;
      case CompareType.ContributionPercent:
        dispatch(
          calculateBalanceAsync(
            CONTRIBUTION_RATES.map((data) => ({
              ...request,
              kiwiSaverEmployeeContribution: data.value,
            }))
          )
        );
        break;
      case CompareType.ContributionDollars:
        const contributions: number[] = [request.kiwiSaverVoluntaryAnnualContribution!, request.kiwiSaverVoluntaryAnnualContribution! * 2, request.kiwiSaverVoluntaryAnnualContribution! * 3];
        setContributionDollarsAllocation(contributions);
        dispatch(
          calculateBalanceAsync(
            contributions.map((kiwiSaverVoluntaryAnnualContribution) => ({
              ...request,
              kiwiSaverVoluntaryAnnualContribution,
            }))
          )
        );
        break;
      case CompareType.RetirementAge:
        dispatch(
          calculateBalanceAsync(
            AGES_RETIREMENT.map((retirementAge) => ({
              ...request,
              retirementAge,
            }))
          )
        );
        break;
      case CompareType.Drawdown:
        dispatch(calculateBalanceAsync(AGES_DRAWDOWN.map((drawdownAge) => ({ ...request, drawdownAge }))));
        break;
    }
  };

  return (
    <FormikWrapper
      initialValues={defaultSettings}
      onSubmit={calculate}
      validationSchema={yup.object({
        ageInScenario: yup.string().required("Age is required"),
        grossAnnualIncome: yup.string().when("employmentStatus", {
          is: EmploymentStatus.Employed,
          then: yup.string().required("Annual salary is required"),
        }),
        kiwiSaverBalance: yup.string().required("KiwiSaver balance is required"),
        kiwiSaverVoluntaryAnnualContribution: yup.string().when("employmentStatus", {
          is: EmploymentStatus.SelfEmployed,
          then: yup.string().required("Annual KiwiSaver contribution is required"),
        }),
      })}
    >
      {({ values, setFieldValue, isValid }) => {
        useEffect(() => {
          isValid && calculate(values);
        }, [values.compareType, values.employmentStatus, values.contributionRate]);

        useEffect(() => {
          setSeries([]);
          sortNumbersDescending([...kiwiSaverBalances.map((c) => c.kiwiSaverBalance || 0)]).forEach((value, index) => {
            const contribution = CONTRIBUTIONS[index];
            let color = "";
            let name: string = "";

            setShowDrawdownTable(values.compareType === CompareType.Drawdown);

            switch (values.compareType) {
              case CompareType.Strategies:
                const strategy = STRATEGIES[index];
                color = strategy.color;
                name = `${strategy.name} Strategy`;
                break;
              case CompareType.ContributionPercent:
                color = contribution ? contribution.color : "transparent";
                name = contribution ? contribution.name : "";
                break;
              case CompareType.ContributionDollars:
                const sortedContributions = sortNumbersDescending(contributionDollarsAllocation);
                const contributionDollar = sortedContributions[index];
                color = contribution.color;
                name = contributionDollar ? contributionDollar.toString() : "";
                break;
              case CompareType.RetirementAge:
                const _retirementAges = sortNumbersDescending(AGES_RETIREMENT);
                const retirementAge = _retirementAges[index];
                color = contribution.color;
                name = retirementAge.toString();
                break;
              case CompareType.Drawdown:
                const drawdownAge = AGES_DRAWDOWN[index];
                color = contribution.color;
                name = drawdownAge.toString();
                calculateDrawdown(value);
                break;
              default:
                break;
            }

            const series: Series = {
              color: color,
              name: name,
              value: value,
              data: [{ y: 0 }, { y: value }, { y: value * 4, marker: defaultMarker({ fillColor: color }) }],
            };

            setSeries((s) => {
              const _series = [...s, series];
              _series.sort((a, b) => (a.value! > b.value! ? -1 : 1));
              return _series;
            });
          });
        }, [kiwiSaverBalances]);

        return (
          <>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TitleText hideMarginBottom>KiwiSaver calculator</TitleText>
                <TitleText hideMarginBottom variant="h6">
                  Accumulation phase
                </TitleText>
              </Grid>
              <Grid item md={4} sm={12}>
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <NumberTextInput
                      name="ageInScenario"
                      label="Age"
                      mask="99"
                      setStringValue
                      className={productClasses.borderColor}
                      onBlur={() => {
                        if (isValid && prevValues?.ageInScenario !== values.ageInScenario) calculate(values);
                        updateAge?.(values.ageInScenario);
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Select
                      name="employmentStatus"
                      label="Employment status"
                      className={productClasses.borderColor}
                      options={[EmploymentStatus.Employed, EmploymentStatus.SelfEmployed]}
                      onChange={(_) => {
                        setFieldValue("compareType", CompareType.Strategies);
                        setFieldValue("kiwiSaverVoluntaryAnnualContribution", 3000);
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <CurrencyTextInput
                      name="kiwiSaverBalance"
                      label="KiwiSaver balance"
                      className={productClasses.borderColor}
                      onBlur={() => {
                        if (isValid && prevValues?.kiwiSaverBalance !== values.kiwiSaverBalance) calculate(values);
                        updateKiwiSaverBalance && updateKiwiSaverBalance(values.kiwiSaverBalance);
                      }}
                    />
                  </Grid>
                  {values.employmentStatus === EmploymentStatus.Employed ? (
                    <>
                      <Grid item xs={12}>
                        <CurrencyTextInput
                          name="grossAnnualIncome"
                          label="Annual salary"
                          className={productClasses.borderColor}
                          onBlur={() => {
                            if (isValid && prevValues?.grossAnnualIncome !== values.grossAnnualIncome) calculate(values);
                            updateAnnualIncome && updateAnnualIncome(values.grossAnnualIncome);
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Select name="contributionRate" label="Contribution rate" options={CONTRIBUTION_RATES} className={productClasses.borderColor} />
                      </Grid>
                    </>
                  ) : (
                    <Grid item xs={12}>
                      <CurrencyTextInput
                        name="kiwiSaverVoluntaryAnnualContribution"
                        label="Annual KiwiSaver contribution"
                        className={productClasses.borderColor}
                        onBlur={() => isValid && prevValues?.kiwiSaverVoluntaryAnnualContribution !== values.kiwiSaverVoluntaryAnnualContribution && calculate(values)}
                      />
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <Select
                      label="Graph type"
                      name="compareType"
                      options={getComparisonOptions(values.ageInScenario, values.retirementAge, values.employmentStatus)}
                      className={productClasses.borderColor}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item md={8} sm={12} style={{ overflow: "hidden" }}>
                <Box mt={3}>
                  <Box position="relative">
                    <Chart backgroundColor={backgroundColor} series={series} type="spline" startLabel="Today" endLabel="At retirement" />
                    <Box position="absolute" top={0} left={0} zIndex={2}>
                      {series.map((s, index) => (
                        <Box key={index} mb="7px" ml={1} p={1} bgcolor={s.color}>
                          <Typography variant="h6" style={{ color: Colors.White, fontSize: "1.35rem" }}>
                            <NumberFormat value={Math.round(s.value! / 1000) * 1000} displayType="text" thousandSeparator prefix="$" decimalScale={0} fixedDecimalScale /> |&nbsp;
                            {values.compareType !== CompareType.ContributionDollars ? (
                              s.name
                            ) : (
                              <NumberFormat value={s.name} displayType="text" thousandSeparator prefix="$" decimalScale={0} fixedDecimalScale />
                            )}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <SubtitleText hideMarginBottom>
                  Further information on Risk Profiles can be found{" "}
                  <a target="_blank" href="https://portal.nzfunds.co.nz/RiskLaunch/">
                    here
                  </a>
                  . Click{" "}
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      setShowAssumptions(true);
                      return false;
                    }}
                  >
                    here
                  </a>{" "}
                  to view the accumulation assumptions.
                </SubtitleText>
              </Grid>
              {showDrawdownTable && (
                <>
                  <Grid item xs={12}>
                    <TitleText>Drawdown - rules of thumb</TitleText>
                    <SubtitleText>There are some rules of thumb that can provide some guidance on the amount your KiwiSaver could provide over your retirement.</SubtitleText>
                    <SubtitleText hideMarginBottom>
                      Below are two estimated weekly amounts your KiwiSaver could contribute using two of these different rules of thumb. These are estimates only, before drawing down your retirement
                      funds, we recommend discussing your options with your financial adviser.
                    </SubtitleText>
                  </Grid>
                  <Grid item xs={12}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TitleText hideMarginBottom>
                          <strong>The 6% rule</strong>
                        </TitleText>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <SubtitleText>Each year, take 6% of the starting value of your retirement savings.</SubtitleText>
                        <SubtitleText black hideMarginBottom>
                          <strong>How it works</strong>
                        </SubtitleText>
                        <SubtitleText>You receive the same nominal amount each year but the length of time you receive it for varies.</SubtitleText>
                        <SubtitleText black hideMarginBottom>
                          <strong>Most suitable for</strong>
                        </SubtitleText>
                        <SubtitleText>People who want more income at the start of their retirement, to ‘frontload’ their spending, and are not concerned with inheritance.</SubtitleText>
                      </Grid>
                      <Grid item xs={12} md={8}>
                        <Grid container style={{ alignItems: "flex-end" }}>
                          <Grid item xs={2} style={{ borderBottom: `1px solid ${Colors.Grey}`, paddingBottom: 3 }}>
                            <Typography style={{ color: Colors.Green, fontWeight: 500, lineHeight: 1 }}>Year</Typography>
                          </Grid>
                          <Grid item xs={5} style={{ borderBottom: `1px solid ${Colors.Grey}`, paddingBottom: 3, textAlign: "right" }}>
                            <Typography style={{ color: Colors.Green, fontWeight: 500, lineHeight: 1 }}>
                              Weekly income
                              <br />
                              from KiwiSaver
                            </Typography>
                          </Grid>
                          <Grid item xs={5} style={{ borderBottom: `1px solid ${Colors.Grey}`, paddingBottom: 3, textAlign: "right" }}>
                            <Typography style={{ color: Colors.Green, fontWeight: 500, lineHeight: 1 }}>
                              Remaining
                              <br />
                              balance
                            </Typography>
                          </Grid>
                          {drawdownValues.sixPercent.map(({ remainingBalance, weeklyIncome }, index) => (
                            <React.Fragment key={weeklyIncome}>
                              <Grid item xs={2} style={{ borderBottom: `1px solid ${Colors.Grey}` }}>
                                <TableText>{index}</TableText>
                              </Grid>
                              <Grid item xs={5} style={{ borderBottom: `1px solid ${Colors.Grey}`, textAlign: "right" }}>
                                <TableText>
                                  {weeklyIncome === undefined ? (
                                    "—"
                                  ) : (
                                    <NumberFormat value={weeklyIncome.toFixed(2)} displayType="text" thousandSeparator prefix="$" decimalScale={0} fixedDecimalScale />
                                  )}
                                </TableText>
                              </Grid>
                              <Grid item xs={5} style={{ borderBottom: `1px solid ${Colors.Grey}`, textAlign: "right" }}>
                                <TableText>
                                  <NumberFormat value={remainingBalance.toFixed(2)} displayType="text" thousandSeparator prefix="$" decimalScale={0} fixedDecimalScale />
                                </TableText>
                              </Grid>
                            </React.Fragment>
                          ))}
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs={12} style={{ borderTop: `1px solid ${Colors.Grey}`, marginTop: 21 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TitleText hideMarginBottom>
                          <strong>The fixed date rule</strong>
                        </TitleText>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <SubtitleText>Run your retirement savings down over the period to a set date. </SubtitleText>
                        <SubtitleText black hideMarginBottom>
                          <strong>How it works</strong>
                        </SubtitleText>
                        <SubtitleText>The amount you receive each year varies but the length of time you receive it for is known.</SubtitleText>
                        <SubtitleText black hideMarginBottom>
                          <strong>Most suitable for</strong>
                        </SubtitleText>
                        <SubtitleText>People comfortable with living on other income (for example New Zealand Superannuation) after the set date.</SubtitleText>
                      </Grid>
                      <Grid item xs={12} md={8}>
                        <Grid container style={{ alignItems: "flex-end" }}>
                          <Grid item xs={2} style={{ borderBottom: `1px solid ${Colors.Grey}`, paddingBottom: 3 }}>
                            <Typography style={{ color: Colors.Green, fontWeight: 500, lineHeight: 1 }}>Year</Typography>
                          </Grid>
                          <Grid item xs={5} style={{ borderBottom: `1px solid ${Colors.Grey}`, paddingBottom: 3, textAlign: "right" }}>
                            <Typography style={{ color: Colors.Green, fontWeight: 500, lineHeight: 1 }}>
                              Weekly income
                              <br />
                              from KiwiSaver
                            </Typography>
                          </Grid>
                          <Grid item xs={5} style={{ borderBottom: `1px solid ${Colors.Grey}`, paddingBottom: 3, textAlign: "right" }}>
                            <Typography style={{ color: Colors.Green, fontWeight: 500, lineHeight: 1 }}>
                              Remaining
                              <br />
                              balance
                            </Typography>
                          </Grid>
                          {drawdownValues.fixedDate.map(({ remainingBalance, weeklyIncome }, index) => (
                            <React.Fragment key={weeklyIncome}>
                              <Grid item xs={2} style={{ borderBottom: `1px solid ${Colors.Grey}` }}>
                                <TableText>{index}</TableText>
                              </Grid>
                              <Grid item xs={5} style={{ borderBottom: `1px solid ${Colors.Grey}`, textAlign: "right" }}>
                                <TableText>
                                  {weeklyIncome === undefined ? (
                                    "—"
                                  ) : (
                                    <NumberFormat value={weeklyIncome.toFixed(2)} displayType="text" thousandSeparator prefix="$" decimalScale={0} fixedDecimalScale />
                                  )}
                                </TableText>
                              </Grid>
                              <Grid item xs={5} style={{ borderBottom: `1px solid ${Colors.Grey}`, textAlign: "right" }}>
                                <TableText>
                                  <NumberFormat value={remainingBalance.toFixed(2)} displayType="text" thousandSeparator prefix="$" decimalScale={0} fixedDecimalScale />
                                </TableText>
                              </Grid>
                            </React.Fragment>
                          ))}
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs={12}>
                    {showDrawdownTable && (
                      <SubtitleText hideMarginBottom>
                        Click{" "}
                        <a
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            setShowDrawdownAssumptions(true);
                            return false;
                          }}
                        >
                          here
                        </a>{" "}
                        to view drawdown assumptions.
                      </SubtitleText>
                    )}
                  </Grid>
                </>
              )}
              {!showDrawdownTable && (
                <Grid item xs={12}>
                  <SubtitleText hideMarginBottom>
                    {investmentOption === InvestmentOptions.LifeCycle && values.compareType === CompareType.Strategies && (
                      <>
                        NZ Funds Life Cycle is a mix of all three Strategies and will change depending on your age and stage. This chart shows the estimated outcomes through time of the Strategies
                        which make up NZ Funds Life Cycle.
                      </>
                    )}
                    {investmentOption === InvestmentOptions.LifeCycle && values.compareType === CompareType.RetirementAge && (
                      <>Comparisons are based on investment in the NZ Funds KiwiSaver Life Cycle investment option.</>
                    )}
                    {investmentOption === InvestmentOptions.LifeCycle && (values.compareType === CompareType.ContributionDollars || values.compareType === CompareType.ContributionPercent) && (
                      <>
                        NZ Funds Life Cycle is a mix of the Strategies that make up the NZ Funds KiwiSaver Scheme. Your allocation to each Strategy changes over time based on your age and stage. The
                        chart shows the estimated difference that increasing your KiwiSaver contributions makes to your investment outcome at retirement if you invest using NZ Funds Life Cycle.
                      </>
                    )}
                    {investmentOption === InvestmentOptions.SelfSelect && values.compareType === CompareType.Strategies && (
                      <>
                        This chart shows the estimated outcomes through time of the Strategies that make up the NZ Funds KiwiSaver Scheme. Your adviser will confirm your allocations to each of these
                        Strategies.
                      </>
                    )}
                    {investmentOption === InvestmentOptions.SelfSelect &&
                      (values.compareType === CompareType.RetirementAge || values.compareType === CompareType.ContributionDollars || values.compareType === CompareType.ContributionPercent) && (
                        <>Comparisons are based on investment in the NZ Funds KiwiSaver Growth Strategy.</>
                      )}
                  </SubtitleText>
                </Grid>
              )}
              <Grid item xs={12}>
                <SubtitleText hideMarginBottom>
                  For more information see MyFiduciary:{" "}
                  <a target="_blank" href="https://www.nzfunds.co.nz/emailarticles/documents/MyFiduciary_3_per_cent_minimum_KS_contribution.pdf">
                    Is the minimum KiwiSaver contribution sufficient?
                  </a>
                </SubtitleText>
              </Grid>
            </Grid>
            <DialogBox open={showAssumptions} onClose={() => setShowAssumptions(false)} ok={() => setShowAssumptions(false)}>
              <TitleText>How this calculator works</TitleText>
              <SectionTitleText hideMarginBottom hideBorderBottom>
                Compare: Strategies
              </SectionTitleText>
              <p>
                The calculator compares the Income, Inflation, and Growth Strategies in the NZ Funds KiwiSaver Scheme. The comparison shows the impact that asset allocation (the proportion invested in
                income vs growth assets) can have on your retirement savings.
              </p>
              <p>
                For more information on asset allocation, see the current NZ Funds KiwiSaver Scheme Product Disclosure Statement (PDS) at{" "}
                <a target="_blank" href="https://www.nzfunds.co.nz">
                  www.nzfunds.co.nz
                </a>
                .
              </p>
              <SectionTitleText hideMarginBottom hideBorderBottom>
                Compare: Contribution
              </SectionTitleText>
              <p>
                This function looks at the impact of increasing the member's employee contribution rate (for employed members) or the member's voluntary annual contributions (for self-employed
                members). Comparisons are based on investment in the NZ Funds KiwiSaver {investmentOption === InvestmentOptions.SelfSelect ? "Growth Strategy" : "Life Cycle investment option"}.
              </p>
              <SectionTitleText hideMarginBottom hideBorderBottom>
                Compare: Retirement age
              </SectionTitleText>
              <p>
                The calculator assumes an initial retirement age of 65. This function looks at the impact of delaying the member's retirement age beyond 65. All employer and Government contributions
                cease at age 65 (but, as described below, employee and voluntary annual contributions continue). Comparisons are based on investment in the NZ Funds KiwiSaver{" "}
                {investmentOption === InvestmentOptions.SelfSelect ? "Growth Strategy" : "Life Cycle investment option"}.
              </p>
              <SectionTitleText hideMarginBottom hideBorderBottom>
                Employment status: Employed
              </SectionTitleText>
              <p>
                Assumes monthly employee contributions as a percentage of salary, employer contributions of 3% less Employer Superannuation Contribution Tax (ESCT), no additional voluntary
                contributions, and an initial retirement age of 65 (salary increases over time at 3.5% per annum; no other income sources are considered).
              </p>
              <SectionTitleText hideMarginBottom hideBorderBottom>
                Employment status: Self-employed
              </SectionTitleText>
              <p>Assumes an annual voluntary contribution paid at the beginning of each year, no employer contribution, and an initial retirement age of 65.</p>
              <SectionTitleText hideMarginBottom hideBorderBottom>
                Returns
              </SectionTitleText>
              <p>The calculator uses retirement savings projections from the Financial Markets Conduct Regulations 2014 Schedule 7A.</p>
              <Table className={classes.table}>
                <TableBody>
                  <TableRow>
                    <TableCell align="center">
                      <p>
                        <b>Defensive</b>
                      </p>
                      <p>NZ Funds Income Strategy return</p>
                    </TableCell>
                    <TableCell align="center">
                      <p>
                        <b>Growth</b>
                      </p>
                      <p>NZ Funds Inflation Strategy return </p>
                    </TableCell>
                    <TableCell align="center">
                      <p>
                        <b>Aggressive</b>
                      </p>
                      <p>NZ Funds Growth Strategy return</p>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell align="center">
                      <p>1.50%</p>
                    </TableCell>
                    <TableCell align="center">
                      <p>4.50%</p>
                    </TableCell>
                    <TableCell align="center">
                      <p>5.50%</p>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
              <SectionTitleText hideMarginBottom hideBorderBottom>
                Other assumptions
              </SectionTitleText>
              <p>
                Employee contributions and voluntary annual contributions continue each year until retirement without any savings suspension or other non-contributing periods.
                <br />
              </p>
              <p>
                No withdrawals are made prior to retirement.
                <br />
              </p>
              <p>
                Employee contributions, voluntary annual contributions, and employer contributions (where applicable), increase over time at 3.5% per annum (the assumed wage and salary inflation
                rate).
                <br />
              </p>
              <p>
                The calculator is based on current Government policies and assumes the amounts of, and rules around, Government and employer contributions remain unchanged.
                <br />
              </p>
              <p>All amounts are shown in current dollars.</p>
              <SectionTitleText hideMarginBottom hideBorderBottom>
                Important
              </SectionTitleText>
              <p>
                The results obtained through the calculator are not guaranteed to occur, and the calculator is simply a tool to help you understand how choices you make may affect the value of your
                KiwiSaver savings at retirement. The outcomes presented in the calculator are illustrative only.
                <br />
              </p>
              <p>
                The calculator is intended as a guide only and does not constitute investment advice to any person. Before acting on the results of this calculator, you should consider your investment
                objectives, financial situation and needs, and if necessary, seek appropriate professional or financial advice
                <br />
              </p>
              <p>To the extent permitted by law, neither NZ Funds nor any of its related parties accept any responsibility or liability arising from the use of this calculator.</p>
            </DialogBox>
            <DialogBox open={showDrawdownAssumptions} onClose={() => setShowDrawdownAssumptions(false)} ok={() => setShowDrawdownAssumptions(false)}>
              <TitleText>How the drawdown section of the calculator works</TitleText>
              <SectionTitleText hideMarginBottom hideBorderBottom>
                Rules of thumb
              </SectionTitleText>
              <p>
                Rules of thumb are simple guidelines that can be used in savings and retirement planning. They offer general illustrative guidance, providing a rough idea of how to work towards your
                financial goals. They can be helpful either when you don’t have access to personalised financial advice or to support your discussions with a financial adviser.
              </p>
              <p>
                In this context, we have used the document titled “Decumulation Options in the New Zealand Market: How Rules of Thumb Can Help” by the Retirement Income Interest Group of the New
                Zealand Society of Actuaries that discuss how these Rules of Thumb can be applied in the New Zealand market to assist people in making decisions about their retirement savings. You can
                find the full document available at this link:
                <br />
                <a target="_blank" href="https://actuaries.org.nz/content/uploads/2022/03/NZSA-full-doc.pdf">
                  https://actuaries.org.nz/content/uploads/2022/03/NZSA-full-doc.pdf
                </a>
              </p>
              <SectionTitleText hideMarginBottom hideBorderBottom>
                Rate of Return and Inflation
              </SectionTitleText>
              <p>
                When calculating the drawdown tables, we’ve relied on conservative estimates for investment returns and inflation. The assumptions we have used to calculate the projections are set by
                the Government and will be reviewed from time to time. These assumptions around the rate of return are based on your fund type. For the purposes of the drawdown calculator, we have
                chosen a <b>Conservative Fund.</b>
              </p>
              <p>The rates of return are:</p>
              <p>After tax of 28%. This is the highest and most common tax rate for KiwiSaver members.</p>
              <p>Your balance will earn a 2.5% rate of return each year (after fees and tax).</p>
              <p>
                The projections are adjusted for inflation, to enable you to assess the buying power of your money at the time you would receive it. The inflation assumption is currently 2% per annum.
              </p>
              <p>For the weekly amount, you will make regular withdrawals over 25 years (i.e. until age 90) until your balance reaches zero.</p>
              <SectionTitleText hideMarginBottom hideBorderBottom>
                Return after fees and tax have been deducted
              </SectionTitleText>
              <Table className={classes.table}>
                <TableBody>
                  <TableRow>
                    <TableCell align="center">
                      <p>
                        <b>Type of fund*</b>
                      </p>
                    </TableCell>
                    <TableCell align="center">
                      <p>
                        <b>Mix*</b>
                      </p>
                      <p>
                        <b>% in growth assets</b>
                      </p>
                    </TableCell>
                    <TableCell align="center">
                      <p>
                        <b>Assumed rate of return</b>
                      </p>
                      <p>
                        <b>(after fees and tax)</b>
                      </p>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell align="center">
                      <p>Conservative</p>
                    </TableCell>
                    <TableCell align="center">
                      <p>10 – 34.9%</p>
                    </TableCell>
                    <TableCell align="center">
                      <p>2.5%</p>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
              <p>
                * Most funds are a mix of income assets (cash and bonds) and growth assets (shares, property and other assets). Your fund might hold a different asset mix, be a single sector fund, or
                you might be invested in multiple funds. In these cases your provider must use the rate of return that represents the mix of growth and income assets you have.
              </p>
              <p>
                The full document is available at this link:
                <br />
                <a target="_blank" href="https://www.fma.govt.nz/consumer/kiwisaver-and-superannuation/about-kiwisaver/kiwisaver-projections">
                  https://www.fma.govt.nz/consumer/kiwisaver-and-superannuation/about-kiwisaver/kiwisaver-projections
                </a>
              </p>
              <SectionTitleText hideMarginBottom hideBorderBottom>
                Important
              </SectionTitleText>
              <p>
                The results obtained through the calculator are not guaranteed to occur, and the calculator is simply a tool to help you understand how choices you make may affect the value of your
                KiwiSaver savings at retirement. The outcomes presented in the calculator are illustrative only.
              </p>
              <p>
                The calculator is intended as a general illustrative guide only and does not constitute investment advice to any person. Before acting on the results of this calculator, you should
                consider your investment objectives, time horizon, financial situation and needs and consider seeking appropriate professional or financial advice.
              </p>
              <p>To the extent permitted by law, neither NZ Funds nor any of its related parties accept any responsibility or liability arising from the use of this calculator.</p>
            </DialogBox>
          </>
        );
      }}
    </FormikWrapper>
  );
}

const useStyles = makeStyles(
  {
    table: {
      "& .MuiTableCell-root": {
        border: `1px solid ${Colors.Grey}`,
      },
    },
  },
  { name: "Calculator" }
);
